import { useEffect, useState } from "react";
import { Row, Col, Card, Table, Dropdown, Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faDollarSign, faVenus, faMars, faClock, faFilter, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Pie, Bar, Line } from "react-chartjs-2";
import API from "../api";

const DashboardRRHH = () => {
  const [datos, setDatos] = useState([]);
  const [filtrados, setFiltrados] = useState([]);

  const [areas, setAreas] = useState([]);
  const [generos, setGeneros] = useState([]);
  const [anios, setAnios] = useState([]);

  const [fAreas, setFAreas] = useState([]);
  const [fGeneros, setFGeneros] = useState([]);
  const [fAnios, setFAnios] = useState([]);
  const [rango, setRango] = useState([0, 0]);
  const [sueldoLimites, setSueldoLimites] = useState([0, 0]);

  const [promEdad, setPromEdad] = useState(0);
  const [promSueldo, setPromSueldo] = useState(0);
  const [totalSueldo, setTotalSueldo] = useState(0);
  const [tiempoProm, setTiempoProm] = useState(0);
  const [genero, setGenero] = useState({ Masculino: 0, Femenino: 0 });
  const [resumenPorArea, setResumenPorArea] = useState([]);
  const [coloresPorArea, setColoresPorArea] = useState({});
  const [relacionEdadSueldo, setRelacionEdadSueldo] = useState([]);

  useEffect(() => {
    fetch(`${API}/usuarios`)
      .then(res => res.json())
      .then(data => {
        setDatos(data);
        setFiltrados(data);

        const areas = [...new Set(data.map(d => d["Área o Departamento"]))].sort((a, b) =>
          a.localeCompare(b)
        );
        const generos = [...new Set(data.map(d => d["Género"]))];

      const anios = [...new Set(
        data
          .map(d => d["FechaIngreso"]?.split("/")[2])
          .filter(a => a && !isNaN(a))
      )].sort((a, b) => a - b);

        const sueldos = data.map(d => parseFloat(d["SueldoBase"])).filter(Boolean);

        setAreas(areas);
        setGeneros(generos);
        setAnios(anios.sort((a, b) => b - a));
        setRango([Math.min(...sueldos), Math.max(...sueldos)]);
        setSueldoLimites([Math.min(...sueldos), Math.max(...sueldos)]);

        calcularResumen(data);
      });
  }, []);

  const toggleCheckbox = (valor, lista, setLista) => {
    if (lista.includes(valor)) setLista(lista.filter(v => v !== valor));
    else setLista([...lista, valor]);
  };

  const aplicarFiltros = () => {
    let filtrado = [...datos];
    if (fAreas.length > 0) filtrado = filtrado.filter(d => fAreas.includes(d["Área o Departamento"]));
    if (fGeneros.length > 0) filtrado = filtrado.filter(d => fGeneros.includes(d["Género"]));
    if (fAnios.length > 0) filtrado = filtrado.filter(d => fAnios.includes(d["FechaIngreso"]?.split("/")[2]));
    filtrado = filtrado.filter(d => {
      const sueldo = parseFloat(d["SueldoBase"]);
      return sueldo >= rango[0] && sueldo <= rango[1];
    });
    setFiltrados(filtrado);
    calcularResumen(filtrado);
  };

  const quitarFiltros = () => {
    setFAreas([]);
    setFGeneros([]);
    setFAnios([]);
    setRango(sueldoLimites);
    setFiltrados(datos);
    calcularResumen(datos);
  };

  const calcularResumen = (usuarios) => {
    const hoy = new Date();
    const sueldos = usuarios.map(u => parseFloat(u["SueldoBase"] || 0));
    const edades = usuarios.map(u => {
      const [d, m, y] = u["FechaNacimiento"]?.split("/") || [];
      return hoy.getFullYear() - parseInt(y);
    });

    const tiempos = usuarios.map(u => {
      const [d, m, y] = u["FechaIngreso"]?.split("/") || [];
      return hoy.getFullYear() - parseInt(y);
    });

    const generosContados = { Masculino: 0, Femenino: 0 };
    usuarios.forEach(u => {
      if (u["Género"] === "Masculino") generosContados.Masculino++;
      else if (u["Género"] === "Femenino") generosContados.Femenino++;
    });

    const resumen = {};
    usuarios.forEach(u => {
      const area = u["Área o Departamento"];
      if (!resumen[area]) resumen[area] = { totalTiempo: 0, totalSueldo: 0, cantidad: 0 };
      const [d, m, y] = u["FechaIngreso"]?.split("/") || [];
      const anios = hoy.getFullYear() - parseInt(y);
      resumen[area].totalTiempo += anios;
      resumen[area].totalSueldo += parseFloat(u["SueldoBase"]);
      resumen[area].cantidad++;
    });

    const resumenFinal = Object.entries(resumen).map(([area, info]) => ({
      area,
      tiempoPromedio: (info.totalTiempo / info.cantidad).toFixed(1),
      sueldoProm: (info.totalSueldo / info.cantidad).toFixed(0),
      totalSueldo: info.totalSueldo
    }));

    const relacion = usuarios.map(u => {
      const [d, m, y] = u["FechaNacimiento"]?.split("/") || [];
      const edad = hoy.getFullYear() - parseInt(y);
      const sueldo = parseFloat(u["SueldoBase"]);
      return { x: edad, y: sueldo };
    }).filter(Boolean);

    setPromEdad((edades.reduce((a, b) => a + b, 0) / edades.length).toFixed(1));
    setPromSueldo((sueldos.reduce((a, b) => a + b, 0) / sueldos.length).toFixed(0));
    setTotalSueldo(sueldos.reduce((a, b) => a + b, 0));
    setTiempoProm((tiempos.reduce((a, b) => a + b, 0) / tiempos.length).toFixed(1));
    setGenero(generosContados);
    setResumenPorArea(resumenFinal);

    const colores = ["#3366CC", "#DC3912", "#FF9900", "#109618", "#990099", "#0099C6"];
    const coloresAsignados = {};
    resumenFinal.forEach((r, i) => {
      coloresAsignados[r.area] = colores[i % colores.length];
    });
    setColoresPorArea(coloresAsignados);
    setRelacionEdadSueldo(relacion);
  };

  const dataTorta = {
    labels: resumenPorArea.map(r => r.area),
    datasets: [{
      data: resumenPorArea.map(r => r.sueldoProm),
      backgroundColor: resumenPorArea.map(r => coloresPorArea[r.area])
    }]
  };

  const dataBarras = {
    labels: resumenPorArea.map(r => r.area),
    datasets: [{
      label: "Sueldo Promedio",
      data: resumenPorArea.map(r => r.sueldoProm),
      backgroundColor: resumenPorArea.map(r => coloresPorArea[r.area])
    }]
  };

  const dataLinea = {
    datasets: [{
      label: "Relación Sueldo vs Edad",
      data: relacionEdadSueldo,
      backgroundColor: "rgba(255, 99, 132, 0.5)",
      borderColor: "rgba(255, 99, 132, 1)",
      showLine: false,
      pointRadius: 5
    }]
  };

  const opcionesLinea = {
    responsive: true,
    scales: {
      x: { type: "linear", title: { display: true, text: "Edad (años)" } },
      y: { title: { display: true, text: "Sueldo Base ($)" } }
    }
  };

  return (
    <div className="container py-4">

      <Row className="g-3 align-items-end mb-4">
        <Col md={2}>
          <Dropdown autoClose="outside">
            <Dropdown.Toggle variant="outline-dark" className="w-100">Área</Dropdown.Toggle>
            <Dropdown.Menu style={{ maxHeight: "200px", overflowY: "auto" }}>
              {areas.map((area, i) => (
                <Form.Check key={i} type="checkbox" label={area}
                  checked={fAreas.includes(area)}
                  onChange={() => toggleCheckbox(area, fAreas, setFAreas)} />
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
        <Col md={2}>
          <Dropdown autoClose="outside">
            <Dropdown.Toggle variant="outline-dark" className="w-100">Género</Dropdown.Toggle>
            <Dropdown.Menu>
              {generos.map((g, i) => (
                <Form.Check key={i} type="checkbox" label={g}
                  checked={fGeneros.includes(g)}
                  onChange={() => toggleCheckbox(g, fGeneros, setFGeneros)} />
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
        <Col md={2}>
          <Dropdown autoClose="outside">
            <Dropdown.Toggle variant="outline-dark" className="w-100">Año Ingreso</Dropdown.Toggle>
            <Dropdown.Menu style={{ maxHeight: "200px", overflowY: "auto" }}>
              {anios.map((a, i) => (
                <Form.Check key={i} type="checkbox" label={a}
                  checked={fAnios.includes(a)}
                  onChange={() => toggleCheckbox(a, fAnios, setFAnios)} />
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
        <Col md={4}>
          <Form.Label className="w-100 text-center fw-bold">Rango Sueldo</Form.Label>
          <div className="d-flex flex-column align-items-center">
            <div className="d-flex justify-content-between w-100 px-3 mb-1">
              <span>{rango[0].toLocaleString("es-CL")} CLP</span>
              <span>{rango[1].toLocaleString("es-CL")} CLP</span>
            </div>
            <div className="position-relative w-100">
              <Form.Range
                min={sueldoLimites[0]}
                max={sueldoLimites[1]}
                step={10000}
                value={rango[0]}
                onChange={(e) => setRango([parseInt(e.target.value), rango[1]])}
                className="position-absolute w-50"
                style={{ pointerEvents: "none", opacity: 0.6 }}
              />
              <Form.Range
                min={sueldoLimites[0]}
                max={sueldoLimites[1]}
                step={10000}
                value={rango[1]}
                onChange={(e) => setRango([rango[0], parseInt(e.target.value)])}
              />
            </div>
          </div>

        </Col>
        <Col md={1}><Button variant="primary" onClick={aplicarFiltros}><FontAwesomeIcon icon={faFilter} /></Button></Col>
        <Col md={1}><Button variant="danger" onClick={quitarFiltros}><FontAwesomeIcon icon={faTimes} /></Button></Col>
      </Row>

      {/* Tarjetas y gráficos */}
      <Row className="g-4 mb-4">
        <Col md={3}><Card className="text-center shadow"><Card.Body><FontAwesomeIcon icon={faDollarSign} size="2x" className="mb-2 text-primary" /><h5 className="fw-bold">Costo Planilla</h5><p className="fs-5">${totalSueldo.toLocaleString("es-CL")}</p></Card.Body></Card></Col>
        <Col md={3}><Card className="text-center shadow"><Card.Body><FontAwesomeIcon icon={faUsers} size="2x" className="mb-2 text-success" /><h5 className="fw-bold">Total Empleados</h5><p className="fs-5">{filtrados.length}</p></Card.Body></Card></Col>
        <Col md={3}><Card className="text-center shadow"><Card.Body><FontAwesomeIcon icon={faClock} size="2x" className="mb-2 text-warning" /><h5 className="fw-bold">Promedio Edad</h5><p className="fs-5">{promEdad} años</p></Card.Body></Card></Col>
        <Col md={3}><Card className="text-center shadow"><Card.Body><FontAwesomeIcon icon={faDollarSign} size="2x" className="mb-2 text-danger" /><h5 className="fw-bold">Prom. Sueldo Base</h5><p className="fs-5">${parseInt(promSueldo).toLocaleString("es-CL")}</p></Card.Body></Card></Col>
      </Row>

      <Row className="g-4 mb-4">
        <Col md={6}><Card className="shadow"><Card.Body><h5 className="fw-bold mb-3 text-center">Distribución por Área</h5><Pie data={dataTorta} /></Card.Body></Card></Col>
        <Col md={6}>
          <Row className="g-4">
            <Col md={12}><Card className="text-center shadow"><Card.Body><h5 className="fw-bold mb-3">Distribución por Género</h5><div className="d-flex justify-content-around"><div><FontAwesomeIcon icon={faMars} size="2x" className="text-primary" /><p>Hombres: {genero.Masculino}</p></div><div><FontAwesomeIcon icon={faVenus} size="2x" className="text-danger" /><p>Mujeres: {genero.Femenino}</p></div></div></Card.Body></Card></Col>
            <Col md={12}><Card className="text-center shadow"><Card.Body><h5 className="fw-bold mb-3">Tiempo Promedio en el Cargo</h5><p className="fs-4">{tiempoProm} años</p></Card.Body></Card></Col>
            <Col md={12}><Card className="text-center shadow"><Card.Body><h5 className="fw-bold mb-3">Sueldo Promedio por Área</h5><Bar data={dataBarras} options={{ indexAxis: 'y' }} /></Card.Body></Card></Col>
          </Row>
        </Col>
      </Row>

      <Row className="g-4">
        <Col md={6}><Card className="shadow mt-4"><Card.Body><h5 className="fw-bold mb-3 text-center">Resumen por Área</h5><Table striped bordered hover responsive className="text-center align-middle"><thead className="table-secondary"><tr><th>Área</th><th>Tiempo Promedio</th><th>Sueldo Promedio</th><th>Total Sueldos</th></tr></thead><tbody>{resumenPorArea.map((r, i) => (<tr key={i}><td>{r.area}</td><td>{r.tiempoPromedio} años</td><td>${parseInt(r.sueldoProm).toLocaleString("es-CL")}</td><td>${parseInt(r.totalSueldo).toLocaleString("es-CL")}</td></tr>))}</tbody></Table></Card.Body></Card></Col>
        <Col md={6}><Card className="shadow"><Card.Body><h5 className="fw-bold mb-3 text-center">Relación Sueldo vs Edad</h5><Line data={dataLinea} options={opcionesLinea} type="scatter" /></Card.Body></Card></Col>
      </Row>
    </div>
  );
};

export default DashboardRRHH;

